<template>
  <div id="neighborhood-detail" :class="'community-' + route.params.community">
    <MoleculesLoadingOverlay v-if="loading" />
    <div v-else>
      <OrganismsUpcomingEventBanner
        v-if="upcomingEvent.uri"
        :organism="upcomingEvent"
      />
      <OrganismsBWBoxOverlay
        :organism="brandWindow"
        :class="hideTabbedContent ? 'two-col-buttons' : 'three-col-buttons'"
      />
      <OrganismsFlexibleContentColumns
        id="icon-columns"
        :organism="neighborhoodFeatures"
      />
      <!-- Disabled due to client request -->
      <!-- <OrganismsSimpleCentered :organism="simpleCentered" /> -->
      <div>
        <OrganismsSplitContent
          v-for="item in alternatingContent"
          :organism="item"
        />
      </div>
      <OrganismsGalleryGrid
        id="featured-photos-gallery"
        v-if="galleryGrid"
        class="mx-auto my-24 bg-gray-900"
        :organism="galleryGrid"
      />
      <OrganismsTabbedContent
        v-if="!hideTabbedContent"
        id="see-homes"
        :key="tabbedContentKey"
        :organism="tabbedContent"
      >
        <template v-for="item in tabbedContent.item" #[item.id]>
          <h2
            v-if="item.id === 'floorplans'"
            class="text-2xl leading-7 sm:leading-10 sm:text-[32px] font-bold font-archer text-center text-gray-900 pt-12"
          >
            {{
              getNeighborhoodNavSubtitle(
                neighborhood.types,
                getRoundedPrice(neighborhood.basePrice),
                neighborhood.neighborhood?.status,
                true,
              )
            }}
          </h2>

          <h2
            v-if="showNoResultsText(productsByCollection)"
            class="pt-12 pb-16 text-xl text-center text-gray-900 no-results-text font-ubuntu"
          >
            There are currently no {{ item.name }} at this neighborhood. Please
            choose a different tab above.
          </h2>
          <div
            v-for="collection in neighborhoodCollections"
            class="pb-12 last:pb-28 mx-auto max-w-[1220px]"
          >
            <AtomsCollectionButton
              v-if="!showNoResultsText(productsByCollection)"
              :collection="collection"
              :class="[
                'flex items-center justify-center mx-auto !my-0 !py-12 text-center [&>span]:font-bold [&>span]:text-[32px] [&>img]:w-11 w-fit',
              ]"
              disableShadow
              coloredText
            />
            <OrganismsProductGrid
              v-if="
                collection?.includes('easyhouse') &&
                productsByCollection?.easyHouse?.length
              "
              :organism="productsByCollection.easyHouse"
              :type="productsByCollection.type"
              :neighborhood="neighborhood"
            />
            <OrganismsProductGrid
              v-if="
                collection?.includes('wee-cottage') &&
                productsByCollection?.weeCottage?.length
              "
              :organism="productsByCollection.weeCottage"
              :type="productsByCollection.type"
              :neighborhood="neighborhood"
            />
            <OrganismsProductGrid
              v-if="
                collection.includes('limited-edition') &&
                productsByCollection.limitedEdition.length
              "
              :organism="productsByCollection.limitedEdition"
              :type="productsByCollection.type"
              :neighborhood="neighborhood"
            />
            <OrganismsProductGrid
              v-if="
                collection.includes('new-beginnings') &&
                productsByCollection.newBeginnings.length
              "
              :organism="productsByCollection.newBeginnings"
              :type="productsByCollection.type"
              :neighborhood="neighborhood"
            />
          </div>
        </template>
      </OrganismsTabbedContent>
      <div class="mx-auto max-w-screen-xl px-8">
        <Organisms :organisms="organisms" />
      </div>
      <OrganismsSiteMapCTA
        id="site-map"
        v-if="siteMapCTA.link1"
        :organism="siteMapCTA"
      />
      <OrganismsFormCTA :organism="formCTA" />
      <OrganismsMapWithInfo id="map-with-info" :organism="mapWithInfo" />
      <OrganismsPopularNeighborhoods :organism="popularNeighborhoods" />
      <OrganismsTextCTA
        id="cta-be-the-first"
        class="pt-[60px] pb-12 mx-auto max-w-screen-xl my-20"
        :organism="beTheFirst"
      />
      <OrganismsModalForm>
        <header class="mb-4">
          <h2 class="mb-4 text-4xl font-semibold text-black font-archer">
            Be the First
          </h2>
          <p v-if="!isFormSubmitted" class="text-lg text-gray-500">
            Please provide us your information and we will get back to you.
          </p>
        </header>
        <FormsHubSpotForm
          :form="{ id: '767c334f-0893-4757-8fa6-fd4b253bd691' }"
          @submit="isFormSubmitted = true"
        />
      </OrganismsModalForm>
    </div>
    <OrganismsModalLogin />
  </div>
</template>

<style>
#neighborhood-detail {
  &.community-lanterns-at-rock-creek {
  }

  .brand-window-box-overlay .buttons {
    @apply md:!grid md:grid-cols-3 mx-auto;

    a {
      @apply !text-[15px] leading-4 uppercase font-medium px-6 md:whitespace-nowrap;
    }
  }
  .brand-window-box-overlay.two-col-buttons .buttons {
    @apply md:grid-cols-2;

    .button-1-wrapper {
      @apply hidden;
    }
  }

  #icon-columns {
    @apply text-center text-white pt-10 pb-14 px-8;

    img {
      @apply w-14 inline-block pb-4;
    }

    .feature-title {
      @apply font-arial text-lg uppercase font-bold;
    }

    .description {
      @apply font-arial text-lg;
    }
  }

  .image img {
    @apply w-[1147px] max-w-full;
  }

  .tabbed-content {
    header {
      background-image: linear-gradient(101deg, #367ea0 0%, #4dadce 97%);
      .header-content {
        @apply max-w-[556px];
      }
    }
    .tabs-component-tabs {
      @apply w-[556px] mx-auto;
    }

    .tabs-component-panels {
      @apply mb-0;
    }
  }

  #community-dynamic-organisms {
    @apply sm:pt-0;
  }

  .wysiwyg-block {
    h1 {
      @apply sm:max-w-[80%] max-w-full m-auto text-3xl font-semibold md:text-5xl font-archer;
    }
  }

  .simple-centered {
    @apply sm:mb-20 mb-10;
  }

  .split-content {
    @apply sm:mb-20 mb-10 py-0;
  }

  .tabs-component-panels {
    @apply sm:mb-20 mb-10;
  }

  #community-dynamic-organisms {
    .brand-window-text-overlay {
      h1 {
        @apply mb-0 text-gray-800 drop-shadow-none sm:text-[3rem] sm:leading-[3.25rem];
      }
      h2 {
        @apply mb-4 text-bc-blue sm:text-[3rem] font-semibold font-archer drop-shadow-none sm:leading-[3.25rem];
      }
      p {
        @apply text-gray-500 text-xl;
      }
    }
  }
}

.community-baseline {
  .tabbed-content {
    section > div {
      @apply pb-0;
    }
  }
}
</style>

<script setup>
import { useAsyncQuery } from "#imports";
import { GET_NEIGHBORHOOD } from "~/graphql/GetNeighborhood";
import { GET_NEIGHBORHOOD_ORGANISMS } from "~/graphql/GetNeighborhoodOrganisms";
import states from "us-state-converter";
import { storeToRefs } from "pinia";
const route = useRoute();
const breadcrumbs = useBreadcrumbs();
const { breadcrumbsList } = storeToRefs(breadcrumbs);
const { data, pending, error } = await useAsyncQuery(GET_NEIGHBORHOOD, {
  uri: route.path,
});
const neighborhoodDetails = useNeighborhoodDetails();
const { mapInfo } = storeToRefs(neighborhoodDetails);
const modalForm = useModalForm();
const isFormSubmitted = ref(false);
const modalLogin = useModalLogin();
const { modalIsOpen } = storeToRefs(modalLogin);
const tabbedContentKey = ref(0);

const { result } = useQuery(GET_NEIGHBORHOOD_ORGANISMS, { uri: route.path });

const organisms = computed(() => {
  return result.value?.organisms?.hlOrganisms?.hlOrganism ?? [];
});

//console.log(organisms);

// Turn off ssr for community pages
definePageMeta({
  ssr: false,
});

const loading = pending;

const neighborhood = computed(() => {
  return data?.value?.neighborhood ?? [];
});

//console.log(neighborhood.value);

const neighborhoods = computed(() => {
  return data?.value?.neighborhoods?.nodes ?? [];
});

const plans = computed(() => {
  return data?.value?.plans ?? [];
});

const models = computed(() => {
  return data?.value?.models ?? [];
});

const homes = computed(() => {
  return data?.value?.homes ?? [];
});

const locations = computed(() => {
  return data?.value?.locations ?? [];
});

const computedBreadcrumbs = computed(() => {
  return [
    { title: "Our Neighborhoods", link: "/our-neighborhoods/" },
    {
      title: neighborhood.value.title
        ? neighborhood.value.title +
          ", " +
          neighborhood.value.neighborhood?.city
        : "",
    },
  ];
});

watch(computedBreadcrumbs, () => {
  breadcrumbsList.value = computedBreadcrumbs.value;
});

const upcomingEvent = computed(() => {
  return {
    title: neighborhood.value.neighborhood?.upcomingEvent?.title,
    uri: neighborhood.value.neighborhood?.upcomingEvent?.uri,
  };
});

const neighborhoodCollections = computed(() => {
  return getCollectionsByNeighborhood(
    neighborhood.value.slug,
    plans.value.nodes,
    homes.value.nodes,
    models.value.nodes,
    [neighborhood.value.collection?.collection],
  );
});

const neighborhoodFeatures = computed(() => {
  return {
    type: "flexible-content-columns",
    backgroundColor: "#387592",
    containerWidth: "full-width",
    item: neighborhood.value.neighborhood?.neighborhoodFeatures ?? [],
  };
});

const simpleCentered = computed(() => {
  return {
    backgroundColor: "#EEEEEE",
    backgroundImage: {
      sourceUrl:
        neighborhood.value?.neighborhood?.basicContentImage?.sourceUrl ||
        "https://wp.livebouldercreek.com/wp-content/uploads/2022/05/BCN_Brand_2021_easyHouse_04_LG.jpg",
    },
    backgroundImageOpacity: 0.1,
    containerWidth: "full-width",
    title1: "New homes in",
    title2:
      neighborhood.value.neighborhood?.city +
      ", " +
      states.fullName(neighborhood.value.neighborhood?.state),
    text1: neighborhood.value.neighborhood?.description,
  };
});

const alternatingContent = computed(() => {
  return Array.isArray(neighborhood.value.neighborhood?.alternatingContent)
    ? neighborhood.value.neighborhood?.alternatingContent.map((item, index) => {
        return {
          backgroundColor: index % 2 ? "#f0f0f0" : "#ffffff",
          splitContentLayout: index % 2 ? "image-text" : "text-image",
          title1: item.title,
          description: item.description,
          button1: item.button,
          button1Text: item.buttonText,
          link1: item.link,
          videoEmbed: item.videoEmbed,
          image: item.image,
          alt: item.image?.alt,
          fullBleed: true,
        };
      })
    : [];
});

const beTheFirst = {
  title1: "Be the First",
  description:
    "<p>Be the first to know about our Colorado neighborhoods and homes, including early access to pricing, new home plans and VIP events.</p>",
  button1: true,
  button1Text: "Get Updates",
  link1: "#modal-form",
};

const hideTabbedContent = computed(() => {
  const objs = [
    floorplansByCollection?.value,
    modelHomesByCollection?.value,
    quickMoveInByCollection?.value,
  ];
  return objs.every((obj) => {
    return (
      Object.values(obj).filter((arr) => Array.isArray(arr) && arr.length > 0)
        .length === 0
    );
  });
});

const brandWindow = computed(() => {
  return {
    type: "brand-window",
    collectionIcons: neighborhoodCollections.value,
    title1: neighborhood?.value?.title,
    title2: neighborhood.value.neighborhood?.city
      ? neighborhood.value.neighborhood?.city +
        ", " +
        neighborhood.value.neighborhood?.state
      : "",
    title3: getNeighborhoodNavSubtitle(
      neighborhood.value.types,
      getRoundedPrice(neighborhood.value.basePrice),
      neighborhood.value.neighborhood?.status,
      true,
    ),
    text1:
      getNeighborhoodStatusLabel(neighborhood.value.neighborhood?.status) ?? "",
    description: null,
    button1Color: "button-blue",
    button2Color: "button-white",
    button3Color: "button-yellow",
    button1: true,
    button1Text: "See Homes",
    link1: "#see-homes",
    button2: true,
    button2Text: "Get Directions",
    button2Target: "_blank",
    link2: neighborhood.value.neighborhood?.latitude
      ? "https://www.google.com/maps/place/" +
        neighborhood.value.neighborhood?.latitude +
        "," +
        neighborhood.value.neighborhood?.longitude
      : "",
    button3: true,
    button3Text: "View Location",
    link3: "#map-with-info",
    containerWidth: "full-width",
    brandWindowLayout: "box-overlay",
    backgroundImage: neighborhood.value.neighborhood?.thumbnail,
    backgroundVideoFile: neighborhood.value.neighborhood?.backgroundVideoFile,
  };
});

const addTriggers = () => {
  const modalFormTriggers = document.querySelectorAll("a[href$='#modal-form']");

  modalFormTriggers.forEach((node) => {
    // Clone & replace node to remove existing event listeners
    const clonedNode = node.cloneNode(true);
    node.parentNode.replaceChild(clonedNode, node);

    clonedNode.addEventListener("click", (event) => {
      // Add event listener
      event.preventDefault();
      modalForm.openModal();
    });
  });
};

onUpdated(() => {
  addTriggers();
});

const popularNeighborhoods = computed(() => {
  return {
    backgroundColor: "#f0f0f0",
    items: getPopularNeighborhoods(
      neighborhood.value.neighborhood?.popularNeighborhoods,
      neighborhoods.value,
      neighborhood.value.databaseId,
    ),
    plans: plans.value.nodes,
  };
});

const tabbedContent = computed(() => {
  const items = [];

  const showModelsTab = models.value?.nodes?.filter(
    (model) =>
      model.modelDetails?.neighborhood?.slug === neighborhood.value?.slug,
  ).length;

  const showFloorplansTab = plans.value?.nodes?.filter(
    (plan) =>
      plan.floorplanDetails?.neighborhood?.slug === neighborhood.value?.slug,
  ).length;

  const showQMIsTab = homes.value?.nodes?.filter(
    (home) =>
      home.qmiHomeDetails?.neighborhood?.slug === neighborhood.value?.slug,
  ).length;

  if (showModelsTab) {
    items.push({ name: "Model Homes", id: "model-homes" });
  }
  if (showFloorplansTab) {
    items.push({ name: "Floorplans", id: "floorplans" });
  }
  if (showQMIsTab) {
    items.push({ name: "Quick Move-In", id: "quick-move-in" });
  }

  return {
    containerWidth: "default",
    collectionIcons: neighborhoodCollections.value,
    title1: "New homes at " + neighborhood.value.title,
    title2:
      neighborhood.value.neighborhood?.city +
      ", " +
      neighborhood.value.neighborhood?.state,
    item: items,
    defaultTabHash: showFloorplansTab ? "floorplans" : "quick-move-in",
  };
});

const modelHomesByCollection = computed(() => {
  const neighborhoodSlug = neighborhood.value.slug;
  return {
    type: "model-homes",
    easyHouse: models?.value?.nodes?.filter((model) => {
      const collection =
        model.collection?.collection ??
        model.modelDetails?.floorplan?.floorplanDetails?.collection ??
        "";
      const modelNeighborhoodSlug = model.modelDetails?.neighborhood?.slug;
      return (
        collection.includes("easyhouse") &&
        modelNeighborhoodSlug === neighborhoodSlug
      );
    }),
    weeCottage: models?.value?.nodes?.filter((model) => {
      const collection =
        model.collection?.collection ??
        model.modelDetails?.floorplan?.floorplanDetails?.collection ??
        "";
      const modelNeighborhoodSlug = model.modelDetails?.neighborhood?.slug;
      return (
        collection.includes("wee-cottage") &&
        modelNeighborhoodSlug === neighborhoodSlug
      );
    }),
    limitedEdition: models?.value?.nodes?.filter((model) => {
      const collection =
        model.collection?.collection ??
        model.modelDetails?.floorplan?.floorplanDetails?.collection ??
        "";
      const modelNeighborhoodSlug = model.modelDetails?.neighborhood?.slug;
      return (
        collection.includes("limited-edition") &&
        modelNeighborhoodSlug === neighborhoodSlug
      );
    }),
    newBeginnings: models?.value?.nodes?.filter((model) => {
      const collection =
        model.collection?.collection ??
        model.modelDetails?.floorplan?.floorplanDetails?.collection ??
        "";
      const modelNeighborhoodSlug = model.modelDetails?.neighborhood?.slug;
      return (
        collection.includes("new-beginnings") &&
        modelNeighborhoodSlug === neighborhoodSlug
      );
    }),
  };
});

const floorplansByCollection = computed(() => {
  return {
    type: "floorplans",
    easyHouse: plans?.value?.nodes?.filter((plan) => {
      return (
        plan.floorplanDetails?.collection?.includes("easyhouse") &&
        plan.floorplanDetails?.neighborhood?.slug === neighborhood.value.slug
      );
    }),
    weeCottage: plans?.value?.nodes?.filter((plan) => {
      return (
        plan.floorplanDetails?.collection?.includes("wee-cottage") &&
        plan.floorplanDetails?.neighborhood?.slug === neighborhood.value.slug
      );
    }),
    limitedEdition: plans?.value?.nodes?.filter((plan) => {
      return (
        plan.floorplanDetails?.collection?.includes("limited-edition") &&
        plan.floorplanDetails?.neighborhood?.slug === neighborhood.value.slug
      );
    }),
    newBeginnings: plans?.value?.nodes?.filter((plan) => {
      return (
        plan.floorplanDetails?.collection?.includes("new-beginnings") &&
        plan.floorplanDetails?.neighborhood?.slug === neighborhood.value.slug
      );
    }),
  };
});

const quickMoveInByCollection = computed(() => {
  const neighborhoodSlug = neighborhood.value.slug;
  return {
    type: "quick-move-in",
    easyHouse: homes?.value?.nodes?.filter((home) => {
      const collection =
        home.qmiHomeDetails?.floorplan?.floorplanDetails?.collection ??
        home.collection?.collection ??
        "";
      const homeNeighborhoodSlug = home.qmiHomeDetails?.neighborhood?.slug;
      return (
        collection.includes("easyhouse") &&
        homeNeighborhoodSlug === neighborhoodSlug
      );
    }),
    weeCottage: homes?.value?.nodes?.filter((home) => {
      const collection =
        home.qmiHomeDetails?.floorplan?.floorplanDetails?.collection ??
        home.collection?.collection ??
        "";
      const homeNeighborhoodSlug = home.qmiHomeDetails?.neighborhood?.slug;
      return (
        collection.includes("wee-cottage") &&
        homeNeighborhoodSlug === neighborhoodSlug
      );
    }),
    limitedEdition: homes?.value?.nodes?.filter((home) => {
      const collection =
        home.qmiHomeDetails?.floorplan?.floorplanDetails?.collection ??
        home.collection?.collection ??
        "";
      const homeNeighborhoodSlug = home.qmiHomeDetails?.neighborhood?.slug;
      return (
        collection.includes("limited-edition") &&
        homeNeighborhoodSlug === neighborhoodSlug
      );
    }),
    newBeginnings: homes?.value?.nodes?.filter((home) => {
      const collection =
        home.qmiHomeDetails?.floorplan?.floorplanDetails?.collection ??
        home.collection?.collection ??
        "";
      const homeNeighborhoodSlug = home.qmiHomeDetails?.neighborhood?.slug;
      return (
        collection.includes("new-beginnings") &&
        homeNeighborhoodSlug === neighborhoodSlug
      );
    }),
  };
});

const productsByCollection = computed(() => {
  switch (neighborhoodDetails.currentTab) {
    case "floorplans":
      return floorplansByCollection.value;
    case "model-homes":
      return modelHomesByCollection.value;
    case "quick-move-in":
      return quickMoveInByCollection.value;
  }
});

const showCollectionHeader = (iconId, products) => {
  switch (iconId) {
    case "easyhouse":
      return products.easyHouse.length;
    case "wee-cottage":
      return products.weeCottage.length;
    case "limited-edition":
      return products.limitedEdition.length;
    case "new-beginnings":
      return products.newBeginnings.length;
  }
};

const showNoResultsText = (products) => {
  return (
    products?.easyHouse?.length === 0 &&
    products?.weeCottage?.length === 0 &&
    products?.limitedEdition?.length === 0 &&
    products?.newBeginnings?.length === 0
  );
};

const siteMapCTA = computed(() => {
  return {
    containerWidth: "full-width",
    title1:
      "Experience the " +
      neighborhood.value.title +
      " homes for sale in " +
      neighborhood.value.neighborhood.city +
      ", CO",
    backgroundColor: "rgba(137, 136, 48, 0.8)",
    backgroundImage: {
      sourceUrl:
        "https://wp.livebouldercreek.com/wp-content/uploads/2023/04/divider-map-bg.jpg",
    },
    image: {
      sourceUrl:
        "https://wp.livebouldercreek.com/wp-content/uploads/2016/11/siteMap-thumb-color-v3.jpg",
    },
    backgroundImageOpacity: "0.4",
    buttonColors: ["button-white"],
    button1: true,
    button1Text: "See homesites",
    button1Target: "_blank",
    link1: neighborhood.value.neighborhood?.siteMapPdfEPub ?? false,
  };
});

const formCTA = computed(() => {
  return {
    containerWidth: "full-width",
    title1: "Curious about this neighborhood?",
    description: `<p>Whether you're just getting started or ready to schedule a tour, our online team is here to help.</p><p>Call <a href="tel:303-309-0088">303-309-0088</a> or send us a message.</p>`,
    image: {
      sourceUrl: "/img/Alissa-Seth_OSC--300x300.jpg",
    },
    hubspotFormId: "e474d5b6-a106-45e3-9e66-63179ae80735",
    hiddenFormFields: [
      {
        inputSelector: 'input[name="communities_of_interest__c"]',
        inputValue: neighborhood.value.neighborhood?.hubspotValue ?? "",
      },
    ],
  };
});

watch(neighborhood, () => {
  const form =
    document.querySelector("#hsForm_e474d5b6-a106-45e3-9e66-63179ae80735") ??
    false;

  if (!form) return;

  const hiddenFields = [
    {
      inputSelector: 'input[name="communities_of_interest__c"]',
      inputValue: neighborhood.value.neighborhood?.hubspotValue ?? "",
    },
  ];

  if (Array.isArray(hiddenFields)) {
    hiddenFields.forEach((field) => {
      const input = form.querySelector(field.inputSelector);
      input.value = field.inputValue;
    });
  }
});

const mapWithInfo = computed(() => {
  const hours = computed(() => {
    const salesOfficeLocation = Array.isArray(locations?.value?.nodes)
      ? locations?.value?.nodes.find(
          (location) =>
            location?.locationDetails?.neighborhood[0]?.slug ===
            neighborhood.value.slug,
        )
      : {};

    return (
      salesOfficeLocation?.locationDetails?.hours ??
      neighborhood.value.neighborhood?.hours
    );
  });

  return {
    markerTitle: neighborhood.value.title,
    title1: "Stop by for a visit",
    address1: neighborhood.value.neighborhood?.address1,
    address2: neighborhood.value.neighborhood?.address2,
    city: neighborhood.value.neighborhood?.city,
    state: neighborhood.value.neighborhood?.state,
    zip: neighborhood.value.neighborhood?.zip,
    latitude: neighborhood.value.neighborhood?.latitude,
    longitude: neighborhood.value.neighborhood?.longitude,
    hours: hours.value,
    phone: neighborhood.value.neighborhood?.phone,
    salesTeamMembers: neighborhood.value.neighborhood?.saleTeamMembers,
    legal: neighborhood.value.neighborhood?.legal,
  };
});

const scrollToTabs = (currentHash) => {
  const tabs = ["#model-homes", "#floorplans", "#quick-move-in"];
  console.log("scrollToTabs called with:", currentHash); // Log the current hash
  if (tabs.includes(currentHash)) {
    const target = document.querySelector("#see-homes");
    if (target) {
      console.log("Scrolling to #see-homes"); // Log the target element
      target.scrollIntoView(true);
      return true;
    } else {
      console.log("Target #see-homes not found"); // Log if the target is not found
    }
  }
  return false;
};

const mapUrlHash = (currentHash) => {
  const hashMappings = {
    "#tab-floorplans": "#floorplans",
    "#tab-qmi-homes": "#quick-move-in",
    "#tab-model-homes": "#model-homes",
  };
  console.log("mapUrlHash called with:", currentHash); // Log the current hash
  if (hashMappings.hasOwnProperty(currentHash)) {
    const newHash = hashMappings[currentHash];
    console.log("Mapped hash:", newHash); // Log the mapped hash
    window.location.hash = newHash;
    scrollToTabs(newHash);
    return true;
  }
  return false;
};

onMounted(() => {
  const urlHash = window.location.hash;
  console.log("Initial hash:", urlHash); // Log the initial hash
  if (urlHash) {
    mapUrlHash(urlHash);
  }

  window.addEventListener(
    "hashchange",
    () => {
      const urlHash = window.location.hash;
      console.log("Hash changed to:", urlHash); // Log the hash change
      if (urlHash === "#modal-form") {
        modalForm.openModal();
      }
      mapUrlHash(urlHash);
    },
    false,
  );

  addTriggers();
});

watch(loading, async (newLoading, oldLoading) => {
  const urlHash = window.location.hash;
  console.log("Loading changed:", { oldLoading, newLoading }); // Log loading changes

  if (oldLoading && !newLoading) {
    await nextTick();
    const handleTabsHash = scrollToTabs(urlHash) ? true : mapUrlHash(urlHash);

    // Check if urlHash is not empty and the element exists in the document
    if (urlHash && document.querySelector(urlHash) && !handleTabsHash) {
      console.log("Scrolling to element with hash:", urlHash); // Log the element being scrolled to
      document.querySelector(urlHash).scrollIntoView(true);
    }
  }
});

watch(modalIsOpen, (newValue, oldValue) => {
  console.log("Modal open state changed:", { oldValue, newValue }); // Log modal open state changes
  tabbedContentKey.value = tabbedContentKey.value + 1;
});
</script>
